import React, { useState, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { Loader } from "cxa-components";
import { withRouter } from "react-router";
import { Prompt } from "react-router-dom";
import ConvertLeaveBenefits from "./ConvertLeaveBenefits";
import SumAssuredBenefits from "./SumAssuredBenefits";
import PlanBasedBenefits from "./PlanBasedBenefits";
import PlanBasedOptionalBenefits from "./PlanBasedOptionalBenefits";
import { injectIntl } from "react-intl";
import EnrollmentStepper from "./EnrollmentStepper";
import {
  getEnrolmentBenefits,
  saveEnrolmentBenefits,
  validateEnrolmentSelection,
  clearError,
} from "../../../actions/enrollmentActions";
import Wallet from "./WalletPP";
import { prepareSaveData, prepareValidationData } from "./EnrollmentDataUtil";
import messages from "components/messagesDescription";
import withMatchMediaProps from "@hocs/with-match-media-props";
import { ErrorMessage } from "../../common/ErrorMessage";
import { benefitsErrorHandler } from "cxa-utils";
import {
  PLAN_BASED_CONVERT_LEAVE,
  SUM_ASSURED_BASED,
  PLAN_BASED,
  PLAN_BASED_OPTIONAL,
} from "./constants";

const EnrollmentPage = props => {
  const [activeStep, setActiveStep] = useState(0);
  const dispatch = useDispatch();

  useEffect(() => {
    dispatch(getEnrolmentBenefits());
  }, []);

  const enrollment = useSelector(state => state.enrollment);
  const enrollmentSettings = enrollment.settings;
  let enrollmentSteps = enrollmentSettings.enrolmentPages || [];
  const activePage = enrollmentSteps.length > 0 ? enrollmentSteps[activeStep] : null;
  const enrollmentDetail = enrollment.details;

  if (enrollmentDetail && enrollmentDetail.planBasedConvertLeaveBenefits.length === 0) {
    enrollmentSteps = enrollmentSteps.filter(
      step => step.benefitTypeId !== PLAN_BASED_CONVERT_LEAVE
    );
  }
  if (enrollmentDetail && enrollmentDetail.sumAssuredBasedBenefits.length === 0) {
    enrollmentSteps = enrollmentSteps.filter(step => step.benefitTypeId !== SUM_ASSURED_BASED);
  }
  if (enrollmentDetail && enrollmentDetail.planBasedBenefits.length === 0) {
    enrollmentSteps = enrollmentSteps.filter(step => step.benefitTypeId !== PLAN_BASED);
  }
  if (enrollmentDetail && enrollmentDetail.planBasedOptionalBenefits.length === 0) {
    enrollmentSteps = enrollmentSteps.filter(step => step.benefitTypeId !== PLAN_BASED_OPTIONAL);
  }

  const goPrevious = () => {
    dispatch(clearError());
    if (activeStep > 0) {
      setActiveStep(activeStep - 1);
      window.scrollTo({
        top: 0,
        behavior: "smooth",
      });
    }
  };
  const goNext = () => {
    dispatch(clearError());
    if (activeStep < enrollmentSteps.length - 1) {
      setActiveStep(activeStep + 1);
      window.scrollTo({
        top: 0,
        behavior: "smooth",
      });
    }
  };

  const saveEnrolment = () => {
    const data = prepareSaveData(enrollmentDetail);
    dispatch(saveEnrolmentBenefits(data, () => props.push(`/shop/checkout`)));
    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  };
  const validateEnrolment = benefitType => {
    const data = prepareValidationData(enrollmentDetail);
    switch (benefitType) {
      case "sumAssuredBasedBenefits":
        dispatch(validateEnrolmentSelection(data.sumAssuredBasedBenefits, goNext));
        break;
      case "planBasedBenefits":
        dispatch(
          validateEnrolmentSelection(
            [...data.planBasedBenefits, ...data.planBasedOptionalBenefits],
            goNext
          )
        );
        break;
      default:
        break;
    }
  };

  const BenefitList = props => {
    switch (activePage.benefitTypeId) {
      case PLAN_BASED_CONVERT_LEAVE:
        return <ConvertLeaveBenefits {...props} />;
      case SUM_ASSURED_BASED:
        return <SumAssuredBenefits {...props} />;
      case PLAN_BASED:
        return <PlanBasedBenefits {...props} />;
      case PLAN_BASED_OPTIONAL:
        return <PlanBasedOptionalBenefits {...props} />;
      default:
        return "";
    }
  };

  let errorMsg = "";
  if (enrollment.error) {
    const applyErrorHandler = benefitsErrorHandler(props.intl);
    const { messageKey, message, extra } = enrollment.error;
    const extraValue = Array.isArray(extra)
      ? extra.map(
          val =>
            // eslint-disable-next-line react/prop-types
            props.intl.formatMessage({
              id: enrollment.benefitMap[val],
              defaultMessage: enrollment.benefitMap[val],
            }) ||
            // eslint-disable-next-line react/prop-types
            props.intl.formatMessage({
              id: val,
              defaultMessage: val,
            })
        ) // it can be contained 1 or more values
        // eslint-disable-next-line react/prop-types
      : props.intl.formatMessage({
          id: enrollment.benefitMap[extra],
          defaultMessage: enrollment.benefitMap[extra],
        });

    errorMsg = applyErrorHandler({
      message: message || messageKey,
      extraValue: extraValue || extra,
      messageKey,
    });

    errorMsg = <ErrorMessage error={errorMsg} intl={props.intl} />;
  }

  return enrollment.isFetching || enrollment.isSaving || !enrollmentDetail ? (
    <Loader />
  ) : (
    <div>
      <EnrollmentStepper steps={enrollmentSteps} index={activeStep} />
      <Wallet {...props} />
      {activePage && (
        <BenefitList
          {...props}
          activeStep={activeStep}
          goPrevious={goPrevious}
          goNext={goNext}
          saveEnrolment={saveEnrolment}
          validateEnrolment={validateEnrolment}
          errMsg={errorMsg}
        />
      )}
      <Prompt
        when={!enrollment.isSaving && enrollment.isDirty && !props.isNarrowScreen}
        message={() => props.intl.formatMessage({ ...messages.unSaveBenefitMessage })}
      />
    </div>
  );
};

export default withMatchMediaProps({
  isNarrowScreen: {
    maxWidth: 680,
  },
})(withRouter(injectIntl(EnrollmentPage)));



// WEBPACK FOOTER //
// ./components/employee/shop/EnrollmentPage.js